// Тайлы графа маршрутизации: фиксированная сетка 0.05° x 0.03° (~4 x 3.3 км,
// десятки КБ на тайл). Клиент грузит и кэширует тайлы по отдельности и
// склеивает нужные в один граф (mergeChunks) под конкретный отрезок.
import type { BBox, LonLat } from "./routingGraph";

export const TILE_LON = 0.05;
export const TILE_LAT = 0.03;
// Отрезку нужно не больше стольких тайлов (~1300 км2), иначе клиент отказывается.
export const MAX_TILES = 100;

export type TileIndex = [number, number];

export const tileOf = (lon: number, lat: number): TileIndex => [Math.floor(lon / TILE_LON), Math.floor(lat / TILE_LAT)];
export const tileKey = (ix: number, iy: number): string => `${ix}:${iy}`;
export const tileBBox = (ix: number, iy: number): BBox => [ix * TILE_LON, iy * TILE_LAT, (ix + 1) * TILE_LON, (iy + 1) * TILE_LAT];

export function tilesAround(lon: number, lat: number, ring: number): TileIndex[] {
  const [cx, cy] = tileOf(lon, lat);
  const out: TileIndex[] = [];
  for (let x = cx - ring; x <= cx + ring; x++) for (let y = cy - ring; y <= cy + ring; y++) out.push([x, y]);
  return out;
}

const M_PER_DEG = 111_320;

// Тайлы коридора вокруг прямой start-destination шириной marginM в каждую
// сторону (точки вдоль отрезка раз в ~1 км, вокруг каждой - квадрат +-marginM).
// null - тайлов больше MAX_TILES.
export function tilesForLeg(start: LonLat, destination: LonLat, marginM: number): TileIndex[] | null {
  const midLat = (start[1] + destination[1]) / 2;
  const dLat = marginM / M_PER_DEG;
  const dLon = marginM / (M_PER_DEG * Math.cos((midLat * Math.PI) / 180));
  const dx = (destination[0] - start[0]) * M_PER_DEG * Math.cos((midLat * Math.PI) / 180);
  const dy = (destination[1] - start[1]) * M_PER_DEG;
  const steps = Math.max(1, Math.ceil(Math.hypot(dx, dy) / 1000));
  const seen = new Map<string, TileIndex>();
  for (let i = 0; i <= steps; i++) {
    const t = i / steps;
    const lon = start[0] + (destination[0] - start[0]) * t;
    const lat = start[1] + (destination[1] - start[1]) * t;
    const [x0, y0] = tileOf(lon - dLon, lat - dLat);
    const [x1, y1] = tileOf(lon + dLon, lat + dLat);
    for (let x = x0; x <= x1; x++) {
      for (let y = y0; y <= y1; y++) {
        seen.set(tileKey(x, y), [x, y]);
        if (seen.size > MAX_TILES) return null;
      }
    }
  }
  return [...seen.values()];
}
